import { useRoute } from "vue-router";
import { useAppStore } from "@/stores/app.store";
import { getSdkOrigin } from "./use-embed-authentication-broker";
import { ASYNC_FLOW_MESSAGE_EVENT_NAME } from "@f3/shared/async-flow-messages";

interface AuthModalSuccessMessage {
  type: "auth-modal:success";
  sessionToken: string;
  authChannel: "otp" | "oauth";
}

interface AuthModalClosedMessage {
  type: "auth-modal:closed";
}

type AuthModalMessage = AuthModalSuccessMessage | AuthModalClosedMessage;

/**
 * Sends `fanfest:async-flow-message` envelopes to the parent window. Used in
 * two contexts:
 *
 *  1. **Standalone auth popup** — the SDK opens `standalone-auth.page.vue` in
 *     a popup/iframe with an `?origin=` query param. That origin is the
 *     target for `window.parent.postMessage`.
 *
 *  2. **Embedded main frontend** — when `?embed=true`, the frontend is loaded
 *     inside an SDK-managed iframe. The parent origin is discovered during
 *     the embed broker handshake (see `use-embed-authentication-broker.ts`)
 *     and exposed via `getSdkOrigin()`.
 *
 * This helper is a low-level primitive: it assumes the caller only invokes it
 * when a handoff is expected. `useAuthSession` is responsible for gating
 * calls so we don't spam errors from normal in-app flows. If this helper is
 * reached without a resolvable origin, that's treated as a misconfiguration
 * and an error is logged.
 */
export function useAuthModalPostMessage() {
  const route = useRoute();
  const appStore = useAppStore();

  function sendAuthModalMessage(data: AuthModalMessage) {
    const queryOrigin =
      typeof route.query.origin === "string" ? route.query.origin : null;
    const embedOrigin = appStore.isEmbed ? getSdkOrigin() : null;
    const origin = queryOrigin ?? embedOrigin;

    if (!origin) {
      console.error(
        "[AuthModalPostMessage] origin parameter missing (checked route.query.origin and embed SDK origin), refusing to send token",
      );
      return;
    }

    window.parent.postMessage(
      {
        type: ASYNC_FLOW_MESSAGE_EVENT_NAME,
        data,
      },
      origin,
    );
  }

  return { sendAuthModalMessage };
}
